import type { PlainMessage } from "@bufbuild/protobuf";
import type { AgentExecution_Status_OutputValue } from "gitpod-next-api/gitpod/v1/agent_pb";

type PlainOutputValue = PlainMessage<AgentExecution_Status_OutputValue>;

/**
 * Escapes a CSV field according to RFC 4180
 * - Wrap in quotes if contains comma, quote, or newline
 * - Double any internal quotes
 */
function escapeCSVField(field: string): string {
    if (field.includes(",") || field.includes('"') || field.includes("\n")) {
        return `"${field.replace(/"/g, '""')}"`;
    }
    return field;
}

/**
 * Converts a 2D array to CSV string
 */
function arrayToCSV(rows: string[][]): string {
    return rows.map((row) => row.map(escapeCSVField).join(",")).join("\n");
}

/**
 * Triggers browser download of CSV content
 */
export function downloadCSV(content: string, filename: string): void {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
}

/**
 * Formats output value for CSV (matches backend logic)
 */
export function formatOutputValueForCSV(value: PlainOutputValue): string {
    switch (value.value.case) {
        case "stringValue":
            return value.value.value;
        case "intValue":
            return value.value.value.toString();
        case "floatValue":
            // Match Go's %g format: remove trailing zeros
            return parseFloat(value.value.value.toPrecision(10)).toString();
        case "boolValue":
            return value.value.value ? "true" : "false";
        default:
            return "-";
    }
}

/**
 * Generates CSV content from headers and rows
 */
export function generateCSV(headers: string[], rows: string[][]): string {
    return arrayToCSV([headers, ...rows]);
}
